import React from "react";
import { Link } from "react-router-dom";
import LegalLayout from "@/components/LegalLayout";

/**
 * Refund Policy — APPROVED FINAL (May 26, 2026).
 *
 * Covers every paid surface: subscriptions, one-time registrations,
 * certificates, marketplace listing fees, breeding listings, transfer
 * fees, live video tours, the Armstrong family storefront, and the
 * Armstrong Transport marketplace.
 *
 * Refunds for marketplace dog purchases (the actual sale) are governed
 * by §4 of the Terms of Service (Escrow & Dispute Resolution). §8 below
 * summarises that flow; §9 governs Armstrong Transport bookings end-to-end.
 *
 * Section numbering follows the published copy — do not renumber
 * sections without legal review.
 */
export default function RefundPolicy() {
  return (
    <LegalLayout
      slug="refund"
      eyebrow="Policy"
      title="Refund Policy"
      lastUpdated="June 2, 2026"
    >
      <div
        style={{
          margin: "0 0 24px",
          padding: "14px 18px",
          border: "2px solid #C0392B",
          background: "#FDECEC",
          borderRadius: 6,
          fontSize: "0.95rem",
          lineHeight: 1.55,
        }}
        data-testid="refund-venue-banner"
      >
        <p style={{ margin: 0, fontWeight: 700, color: "#7A1F12" }}>
          VENUE POSTURE &mdash; READ BEFORE REQUESTING A REFUND
        </p>
        <p style={{ margin: "8px 0 0", color: "#3B0E07" }}>
          Armstrong K9 Registry is a <strong>venue</strong>. For every
          peer-to-peer transaction arranged on the platform &mdash;
          marketplace dog sales, escrow purchases, waitlist deposits,
          live video tours, transport bookings, storefront purchases,
          <strong> and service-provider bookings (training, grooming,
          dog walking, pet sitting, boarding, day-care, behavior
          consultations, and every other service offered through{" "}
          <Link to="/find-pros">/find-pros</Link>)</strong> &mdash; the
          <strong> counterparty user is the merchant of record</strong> and
          any refund is governed by their published policy. Armstrong
          does not unilaterally refund money that has flowed to a third
          party, and{" "}
          <strong>Armstrong does not mediate, arbitrate, adjudicate,
          investigate, or render decisions on refund disputes between
          users.</strong> We host the platform; we do not stand between
          the parties to a transaction.
        </p>
        <p style={{ margin: "8px 0 0", color: "#3B0E07" }}>
          You use the platform <strong>at your own risk</strong> and your
          recourse against another user is your own contract, your own
          counsel, your own state&rsquo;s consumer-protection statute, and
          (if applicable) a Stripe chargeback through your card issuer.
          This Refund Policy below covers ONLY purchases made directly
          from Armstrong itself (subscriptions, listing fees,
          certificates, AI add-ons, and the limited mechanical
          escrow/timer functions described in §8 and §9).
        </p>
      </div>

      <h2>1. Scope</h2>
      <p>
        This policy describes refunds for purchases made directly from
        Armstrong K9 Registry (&ldquo;we&rdquo;, &ldquo;our&rdquo;). It does
        not cover marketplace dog purchases, storefront purchases, and
        transporter bookings which are governed by the Escrow &amp; Dispute
        Resolution section of our <Link to="/terms">Terms of Service</Link>.
      </p>

      <h2>2. Subscriptions (Breeder &amp; Buyer Pass)</h2>
      <ul>
        <li>
          <strong>7-day no-questions refund window:</strong> Any new monthly
          or annual subscription is fully refundable within 7 calendar days of
          purchase, provided fewer than 5 included registrations have been
          used.
        </li>
        <li>
          <strong>Pro-rated cancellation:</strong> After the 7-day window we
          do not pro-rate annual plans. You may cancel auto-renewal at any
          time from the <Link to="/billing">Billing</Link> page; the plan
          will remain active through the paid period, then expire.
        </li>
        <li>
          <strong>Auto-renew failure:</strong> If a renewal payment fails, the
          plan is converted to its 30-day month-to-month equivalent until the
          payment method is updated, then re-billed at the original rate.
        </li>
      </ul>

      <h2 id="apple-iap">2a. Subscriptions purchased through Apple In-App Purchase (iOS app)</h2>
      <ul>
        <li>
          Subscriptions bought inside the iOS app are billed by{" "}
          <strong>Apple</strong> to your Apple&nbsp;ID &mdash; Apple, not the
          Registry, is the merchant of record for those purchases.{" "}
          <strong>We are unable to issue refunds for Apple In-App
          Purchases</strong>; Section 2 above applies only to subscriptions
          purchased on our website via Stripe.
        </li>
        <li>
          To request a refund for an In-App Purchase, use Apple&rsquo;s
          refund process at{" "}
          <a href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer">
            reportaproblem.apple.com
          </a>{" "}
          or through your App&nbsp;Store purchase history. Apple&rsquo;s
          decision on the request is final and governed by Apple&rsquo;s own
          policies.
        </li>
        <li>
          <strong>Cancelling:</strong> manage or cancel an In-App Purchase
          subscription from your device (Settings &rarr; your name &rarr;
          Subscriptions). Cancelling stops the next renewal; it does not
          refund the current billing period, which stays active until it
          expires.
        </li>
      </ul>

      <h2>3. One-time registrations</h2>
      <ul>
        <li>
          <strong>Dog &amp; litter registration:</strong> Refundable within
          24 hours of purchase as long as the registration certificate has
          not been downloaded or shared. Once the printable certificate is
          generated, the registration is final.
        </li>
        <li>
          <strong>Pedigree certificate ($14.99):</strong> Refundable within
          24 hours if the PDF has not been downloaded.
        </li>
        <li>
          <strong>Ownership transfer ($15):</strong> Refundable only if the
          transfer has not yet been approved by an admin. Once approved, the
          transfer is recorded immutably on the dog&rsquo;s pedigree and the
          fee is non-refundable.
        </li>
      </ul>

      <h2>4. Marketplace listing fees</h2>
      <ul>
        <li>
          <strong>Single-dog &amp; litter listings:</strong> Non-refundable
          once activated. We will, however, transfer remaining listing days
          to a different dog or litter you own on request, one time per
          purchase.
        </li>
        <li>
          <strong>Featured-listing boost:</strong> Non-refundable once the
          listing has been featured for any period. If we fail to surface
          your listing in the featured slot for any reason, we will extend
          the feature window by the lost days at no charge.
        </li>
      </ul>

      <h2>5. Breeding directory listings</h2>
      <p>
        Non-refundable once published. You may delete the listing at any
        time but the listing fee is not pro-rated.
      </p>

      <h2>5a. Upcoming Litter listings &amp; waitlist deposits</h2>
      <p>
        Upcoming Litter listings are a separate product. Two fees apply:
      </p>
      <ul>
        <li>
          <strong>Listing fee ($25 planned breeding / $40 expected litter):</strong>{" "}
          Paid by the breeder to Armstrong. <strong>Non-refundable</strong> once
          the listing publishes to the marketplace. If the listing is removed
          before it goes live for any reason, the fee is refunded in full.
        </li>
        <li>
          <strong>Featured boost ($15 / 30 days):</strong> Non-refundable once
          the listing has been featured for any period.
        </li>
        <li>
          <strong>Waitlist deposits:</strong> Deposits are paid by the buyer{" "}
          <strong>directly to the breeder via Stripe Connect</strong>. The
          breeder is the merchant of record. <strong>Armstrong does not
          hold, escrow, or refund deposits.</strong> Each breeder publishes
          their own refund policy in their listing; that policy is the
          contract between buyer and breeder. Armstrong takes a platform
          fee (5% capped at $25) at the time of the deposit charge; that
          platform fee is non-refundable if the underlying deposit is
          subsequently refunded by the breeder.
        </li>
        <li>
          <strong>Disputes:</strong> Stripe chargebacks on a deposit are
          assessed against the breeder&rsquo;s Stripe Connect account, not
          Armstrong. Buyers seeking a refund must request it from the
          breeder; the &ldquo;Request refund&rdquo; button in the marketplace
          listing notifies the breeder but does not auto-refund any funds.
        </li>
      </ul>

      <h2>6. Live video tour bookings ($35)</h2>
      <p>
        Video tour bookings are 15-minute browser-based audio/video calls
        between a buyer and a verified breeder, powered by our Daily.co
        infrastructure. Refund rules:
      </p>
      <ul>
        <li>
          <strong>Card not charged until seller accepts.</strong> When you
          submit a tour request, no payment is captured. If the seller
          declines or fails to respond within 24 hours, you pay $0.
        </li>
        <li>
          <strong>Cancel before the call.</strong> Either party may cancel up
          to 60 minutes before the scheduled start; the buyer is refunded in
          full (less Stripe processing fees).
        </li>
        <li>
          <strong>Seller no-show = automatic full refund.</strong> If the
          seller fails to join the room within 5 minutes of the scheduled
          time, the system marks the tour <code>seller_no_show</code> and
          issues a full refund to the buyer&rsquo;s original card. No support
          ticket required.
        </li>
        <li>
          <strong>Buyer no-show = no refund.</strong> If the buyer fails to
          join, the booking is forfeit and the seller is paid out their 80%
          share of the fee.
        </li>
        <li>
          <strong>Connection problems mid-call.</strong> If technical issues
          on either side prevent the call from completing, contact support
          within 24 hours. We will review the Daily.co session logs and
          either re-schedule or refund at our discretion.
        </li>
      </ul>

      <h2>7. Armstrong storefront purchases</h2>
      <p>
        Purchases made through the Armstrong family storefront at{" "}
        <Link to="/armstrong">/armstrong</Link> &mdash; dog training
        subscriptions, dog food deliveries, and Printify-fulfilled apparel
        &mdash; follow these rules:
      </p>
      <ul>
        <li>
          <strong>Dog training subscriptions:</strong> Auto-renew monthly.
          The $150 setup deposit is non-refundable once paid. The monthly
          fee is non-refundable; we do not pro-rate cancellations. You may
          cancel auto-renewal at any time from your customer portal &mdash;
          your training continues through the end of the period you&rsquo;ve
          already paid for, then service ends.
        </li>
        <li>
          <strong>Pauses:</strong> If you need to pause training (e.g.,
          travel, temporary leave), email us and we&rsquo;ll pause billing on
          your behalf. Paused periods are not charged but also do not refund
          any unused portion of the month they were paused in.
        </li>
        <li>
          <strong>Dog food pre-orders:</strong> Non-refundable once placed,
          because we order against your purchase. If a delivery is missed or
          damaged on our end we will deliver a replacement at no charge.
        </li>
        <li>
          <strong>Printify-fulfilled merch:</strong> Print-on-demand items
          are non-refundable once submitted to the print partner (typically
          within 15 minutes of payment). If your item arrives damaged or
          misprinted, contact us within 14 days with a photo and we will
          arrange a free reprint with the print partner.
        </li>
      </ul>

      <h2>8. Marketplace dog purchases (escrow)</h2>
      <p>
        Refunds for the actual purchase of a dog through our marketplace
        escrow are governed by mechanically-enforced rules. <strong>Armstrong
        K9 Registry does not adjudicate the merits of any dispute</strong> —
        the platform administers a published timeline and the outcome is
        determined by that timeline, by the parties&rsquo; own actions, or
        by their mutual written agreement.
      </p>
      <ul>
        <li>
          <strong>Funds are held</strong> by the platform until released by
          one of the mechanisms below. The breeder is the merchant of record
          on the underlying sale.
        </li>
        <li>
          <strong>Buyer confirms pickup</strong> → funds release to the
          breeder immediately. This is the normal happy path.
        </li>
        <li>
          <strong>14-day automatic release.</strong> If the buyer takes no
          action within 14 days of payment, funds release automatically to
          the breeder. The clock and a countdown are visible to both parties.
        </li>
        <li>
          <strong>Buyer files dispute within 14 days</strong> → escrow
          freezes. The auto-release clock stops. Both parties are notified.
          From this point the outcome is determined ONLY by:
          <ul>
            <li>
              <em>Mutual written agreement</em> — both parties click
              &ldquo;Agree&rdquo; on the same proposed outcome (full refund,
              full release, or any partial split). The platform mechanically
              executes whatever they agree to. Either party may propose; the
              other party must accept in writing within the platform.
            </li>
            <li>
              <em>30-day default outcome</em> — if no mutual agreement is
              reached within 30 days of the dispute being filed, funds return
              to the buyer in full. This is the published floor; the
              platform enforces it mechanically with no admin involvement.
              <strong>Individual sellers may publish a longer
              window</strong> (e.g. 60-day satisfaction guarantee) under
              §8a; sellers can never shorten the 30-day floor.
            </li>
          </ul>
        </li>
        <li>
          <strong>Armstrong&rsquo;s role is limited to:</strong> hosting the
          dispute messaging channel between the parties, running the
          countdown timer, and executing the mechanical refund or release
          when one of the above conditions is met. We do not read evidence,
          weigh fault, render decisions, or pick sides. We are not a judge,
          arbiter, or mediator of these disputes.
        </li>
        <li>
          <strong>Chargebacks remain available.</strong> A Stripe chargeback
          is assessed against the breeder&rsquo;s Stripe Connect account, not
          Armstrong. Buyers seeking faster recourse than the 30-day default
          may always pursue a chargeback directly with their card issuer.
        </li>
        <li>
          <strong>Sales tax on Dog Shop orders</strong> is calculated and
          collected at checkout via Stripe Tax, transferred to the
          seller along with the order total, and is{" "}
          <strong>non-refundable by the Registry</strong>. The seller is
          the merchant of record and remains responsible for remitting
          the collected tax to the appropriate authorities. Tax-refund
          disputes (e.g. a return on a Dog Shop order) are handled
          directly by the seller; the Registry has no mechanism to
          refund tax once it has been transferred out.
        </li>
        <li>
          <strong>Sales tax on private-treaty dog sales (escrow,
          marketplace listings, waitlist deposits, video tours,
          transport)</strong> is{" "}
          <strong>never collected by the Registry</strong>. The seller
          is the merchant of record. If the seller collected sales tax
          on the side, the buyer must recover it directly from the
          seller.
        </li>
      </ul>

      <h2>8b. Per-seller refund extensions &amp; deposits (Stance C)</h2>
      <p>
        Individual breeders may publish two ADD-ONS to the platform
        baseline above. They can only ADD buyer protection or layer a
        non-refundable deposit on top of it — they can never shorten
        the 30-day mechanical floor or opt out of the buyer&rsquo;s
        right to dispute.
      </p>
      <ul>
        <li>
          <strong>Refund extension (optional).</strong> A breeder may
          publish a longer mutual-consent / auto-resolve window (e.g.
          &ldquo;60-day satisfaction guarantee&rdquo; or &ldquo;90-day
          health refund&rdquo;). When set, the 30-day auto-refund
          deadline is extended to <code>30 + N</code> days for any
          escrow purchase from that breeder. The buyer&rsquo;s rights
          are strictly expanded.
        </li>
        <li>
          <strong>Non-refundable deposit (optional, max 50%).</strong>
          A breeder may declare that a portion of the purchase price
          (up to 50%) is non-refundable in the event the buyer cancels
          BEFORE pickup. The deposit terms must be disclosed on the
          dog&rsquo;s detail page and the buyer must explicitly
          acknowledge the deposit at checkout. The deposit clause does
          NOT apply if (a) the seller fails to deliver, (b) the dog
          arrives in materially misrepresented condition, or (c) any
          consumer-protection statute makes the deposit unenforceable
          in the buyer&rsquo;s jurisdiction. In those cases the
          standard mutual-consent / 30-day floor applies as if no
          deposit had been declared.
        </li>
        <li>
          Sellers cannot publish ANY of the following: &ldquo;no
          refunds,&rdquo; &ldquo;sale is final,&rdquo; &ldquo;all
          claims waived,&rdquo; or any clause that purports to
          eliminate the buyer&rsquo;s right to file a dispute or
          shorten the 30-day floor. Such clauses are void on this
          platform and the platform will ignore them.
        </li>
      </ul>

      <h2>8c. Failed subscription payments (no automatic refund)</h2>
      <p>
        A failed charge is{" "}
        <strong>not a refund</strong> — it just means the charge never
        completed. When your card fails on an auto-renew:
      </p>
      <ul>
        <li>
          Your subscription enters a <em>past due</em> state and your
          benefits pause until payment is restored.
        </li>
        <li>
          Stripe automatically retries the charge up to 3-5 times over
          approximately 7 days (Stripe Smart Retries). You'll receive
          email notifications during the recovery window.
        </li>
        <li>
          A red "Payment failed" banner appears on your Dashboard and
          Billing page with a single-click link to the Stripe Customer
          Portal where you can update your card.
        </li>
        <li>
          If all retries fail, the subscription cancels automatically.
          You can re-subscribe anytime at the current published price.
        </li>
        <li>
          We never refund "lost time" caused by a failed payment — your
          card simply wasn't charged for that period.
        </li>
      </ul>

      <h2 id="service-provider-bookings">8d. Service-provider bookings (trainers, groomers, walkers, sitters)</h2>
      <p>
        Bookings made through{" "}
        <Link to="/find-pros">/find-pros</Link> &mdash; including
        training sessions, grooming appointments, dog-walking jobs,
        pet-sitting / boarding stays, day-care, behavior consultations,
        and any other service offered by a verified service provider
        on the platform &mdash; are <strong>peer-to-peer transactions
        between the buyer (the dog owner) and the independent service
        provider</strong>. The service provider is the merchant of
        record for the booking; Armstrong K9 Registry is the listing
        venue and a payment processor. The Registry does not perform
        services, supervise providers, train animals, custody dogs,
        warrant outcomes, or guarantee provider conduct.
      </p>
      <p>
        <strong>Armstrong K9 Registry does not offer refunds on
        service-provider bookings, and Armstrong K9 Registry does not
        mediate, arbitrate, or adjudicate refund disputes between
        buyers and providers.</strong> Every refund question is between
        the buyer and the provider directly, subject to the
        provider&rsquo;s own published cancellation and refund policy,
        the parties&rsquo; own contract or service agreement, and any
        applicable state or local consumer-protection law.
      </p>

      <h3>8d.1 The provider&rsquo;s published policy is binding</h3>
      <ul>
        <li>
          <strong>Each provider publishes their own cancellation, no-show,
          weather, and refund policy</strong> on their public profile and
          on the booking confirmation. That policy is the contract between
          buyer and provider. Buyers are expected to read it before
          booking; clicking &ldquo;Book&rdquo; acknowledges that policy.
        </li>
        <li>
          <strong>Providers, not Armstrong, decide whether to refund.</strong>{" "}
          The provider receives payouts directly to their own Stripe
          Connect account; they are the merchant of record and the
          only party with the authority and the funds to issue a
          refund. The buyer&rsquo;s refund request must go to the
          provider, not to Armstrong.
        </li>
        <li>
          Providers <em>cannot</em> publish any clause that purports to
          waive the buyer&rsquo;s statutory consumer-protection rights or
          eliminate the right to a Stripe chargeback. Such clauses are
          void on this platform and the platform will ignore them.
        </li>
      </ul>

      <h3>8d.2 What Armstrong does NOT do</h3>
      <ul>
        <li>
          Armstrong <strong>does not refund</strong> service-provider
          booking fees from Armstrong&rsquo;s own funds.
        </li>
        <li>
          Armstrong <strong>does not mediate</strong> disputes between
          buyer and provider. The Registry has no claim-review process,
          no internal appeal, and no human reviewer who weighs evidence
          on either side&rsquo;s behalf.
        </li>
        <li>
          Armstrong <strong>does not adjudicate</strong> whether a service
          was performed, performed competently, or worth the price paid.
          Service quality is between the parties.
        </li>
        <li>
          Armstrong <strong>does not freeze, escrow, or claw back
          payouts</strong> on service-provider bookings. Funds flow
          directly to the provider&rsquo;s Stripe Connect account on
          successful payment.
        </li>
        <li>
          Armstrong <strong>does not custody dogs, leashes, equipment,
          medications, premises, vehicles, or any other property</strong>{" "}
          in connection with a service booking. The provider operates
          their own business from their own premises (or yours) with
          their own equipment.
        </li>
        <li>
          Armstrong <strong>does not carry liability insurance</strong>{" "}
          for service-booking incidents. Each provider is required to
          attest to their own licensing and insurance status at signup;
          buyers should verify both before booking high-risk services
          (boarding, off-leash training, etc.).
        </li>
        <li>
          For <strong>international sellers and providers</strong>, payment
          is a <strong>direct charge on the seller&rsquo;s own Stripe
          account</strong> &mdash; the seller is the merchant of record,
          Armstrong never holds the funds, and no Registry escrow or
          dispute protection applies. Refunds and disputes on those
          purchases are handled solely by the seller (see Terms of
          Service &sect;5i). Buyers acknowledge this at checkout.
        </li>
      </ul>

      <h3>8d.3 What Armstrong DOES do</h3>
      <ul>
        <li>
          Routes the booking payment to the provider via Stripe Connect
          (destination charge or split, depending on the service).
        </li>
        <li>
          Collects a 12% platform fee at the time of charge to fund
          discovery, verification, search, support, and infrastructure.
          The platform fee is non-refundable as a Registry expense (see
          §8d.4) unless the entire booking is refunded by the provider
          and the platform fee is mechanically reversible via Stripe.
        </li>
        <li>
          Surfaces the optional FCRA-compliant Checkr background-check
          badge (see §9a) on cleared providers so buyers can make
          better-informed booking decisions.
        </li>
        <li>
          Provides a messaging channel between buyer and provider for
          coordinating logistics, sharing photos/notes, and discussing
          any concerns. Messages are stored as evidence the parties may
          use in their own dispute, but Armstrong does not read or
          adjudicate them.
        </li>
      </ul>

      <h3>8d.4 Platform fee is non-refundable</h3>
      <p>
        The 12% Armstrong platform fee on a service-provider booking is
        a Registry expense for matching, payment routing, and trust-and-
        safety infrastructure already rendered at the moment of booking.
        It is <strong>not refundable</strong> as Armstrong-issued cash,
        even if the underlying booking is refunded by the provider.
        Where the booking is refunded in full via a Stripe-initiated
        reversal that mechanically returns the platform fee in lock-step
        (transporter-style <code>reverse_transfer=true</code> flows),
        the platform fee is reversed automatically; service-provider
        bookings do not currently use this flow.
      </p>

      <h3>8d.5 Chargebacks (your real recourse if a provider refuses)</h3>
      <p>
        If a provider refuses to issue a refund the buyer believes is
        owed under the provider&rsquo;s own published policy or under
        applicable consumer-protection law, the buyer&rsquo;s recourse
        is a <strong>Stripe chargeback through their card issuer</strong>{" "}
        or small-claims court, not Armstrong. Chargebacks are assessed
        against the provider&rsquo;s Stripe Connect account, not against
        Armstrong; the chargeback decision is made by the card issuer,
        not by us. Buyers should contact the provider in writing first
        (via the Armstrong message thread or directly) and allow 7
        calendar days for a response before opening a chargeback.
      </p>
      <p>
        Providers who receive unjustified chargebacks may appeal them
        through Stripe&rsquo;s dispute-evidence portal. Repeated
        substantiated chargebacks (3+ within a rolling 90-day window)
        result in profile suspension under the Service Provider
        Agreement.
      </p>

      <h3>8d.6 No-show, weather, illness</h3>
      <p>
        No-show, severe-weather rescheduling, provider illness, dog
        illness, and similar exception flows are governed entirely by
        the provider&rsquo;s published policy. Armstrong does not
        impose a platform-wide minimum or maximum on these terms.
        Buyers and providers are encouraged to discuss exception terms
        in the message thread before the booking date.
      </p>

      <h3>8d.7 Provider safety incidents are a matter for the provider, the buyer, and applicable authorities</h3>
      <p>
        If a dog is injured, lost, escapes, becomes ill, or is otherwise
        harmed during the course of a service-provider booking, the
        responsibility rests on the service provider (and, where
        applicable, the buyer) per their service agreement and per
        applicable veterinary, animal-welfare, and tort law. Armstrong
        is not a party to the service agreement, does not custody the
        animal, and is not liable for any incident occurring under a
        provider&rsquo;s care. Buyers should report serious incidents to
        local animal control, their veterinarian, and (where applicable)
        law enforcement &mdash; not to Armstrong. We will, on request,
        provide the parties their own booking records, the message
        thread between them, and the provider&rsquo;s attested
        licensing/insurance disclosures at signup; we will not testify
        as a fact witness or render an opinion on fault.
      </p>

      <h2>9. Independent transporter bookings (Armstrong Transport marketplace)</h2>
      <p>
        The Armstrong Transport marketplace is a listing service connecting
        buyers with independent, USDA-licensed third-party transporters. The
        Registry does not drive vehicles, handle animals, set trip prices, or
        warrant transporter performance. Trips are paid via Stripe Connect
        destination charges to the transporter; funds are held in marketplace
        escrow until delivery is confirmed (or auto-released 48 hours after
        the transporter marks delivered, whichever comes first).
      </p>

      <h3>9.1 Buyer-initiated cancellation (tiered)</h3>
      <p>
        Once a transport has been booked and paid, buyer cancellations are
        subject to a kill-fee tier based on how much advance notice the
        transporter receives:
      </p>
      <ul>
        <li>
          <strong>72+ hours before pickup:</strong> 100% refund (no kill
          fee). Stripe reversal is initiated within 1 business day.
        </li>
        <li>
          <strong>24&ndash;72 hours before pickup:</strong> 75% refund. The
          remaining 25% is paid to the transporter as a kill fee to
          compensate for lost dispatch and scheduling time.
        </li>
        <li>
          <strong>Less than 24 hours before pickup:</strong> 50% refund. The
          remaining 50% is paid to the transporter.
        </li>
        <li>
          <strong>After delivery (or in-transit):</strong> No cancellation is
          possible &mdash; buyers must use the Dispute process below.
        </li>
      </ul>
      <p>
        Pre-payment cancellations (while the order is still gathering bids or
        awaiting payment) are always 100% free with no fee. All refunds are
        processed via Stripe with <code>reverse_transfer=true</code> so the
        transporter&rsquo;s Stripe Connect balance is debited in lock-step
        with the buyer credit; the Registry&rsquo;s 10% platform fee is also
        reversed on full refunds.
      </p>

      <h3>9.2 Transporter-initiated cancellation</h3>
      <p>
        If the transporter cancels a confirmed trip, the buyer receives a
        full refund regardless of timing. The transporter does not collect
        any portion of the fare. If the transporter provides less than 12
        hours of notice (or if the Registry is unable to rebook the trip
        with another verified transporter), a strike is recorded on their
        account; three strikes within a rolling 90-day window result in a
        30-day suspension from the Armstrong Transport marketplace under the
        Transporter Agreement.
      </p>

      <h3>9.3 Disputes</h3>
      <p>
        Either party may open a dispute on a transport order from the time
        the trip is booked through 24 hours after delivery. Opening a dispute
        immediately freezes the escrow — the auto-release clock stops and no
        funds move to the transporter until the dispute is resolved.
        <strong> Armstrong K9 Registry does not adjudicate the merits of
        any transport dispute</strong> — the platform administers a
        published timeline and the outcome is determined by that timeline,
        by the parties&rsquo; own actions, or by their mutual written
        agreement. Resolution falls into one of three mechanical outcomes:
      </p>
      <ul>
        <li>
          <strong>Mutual written agreement</strong> — both parties click
          &ldquo;Agree&rdquo; on the same proposed outcome (full release to
          the transporter, full refund to the buyer, or any partial split
          from 1% to 99%). The platform mechanically executes whatever the
          parties agree to. Either party may propose; the other must accept
          in writing within the platform.
        </li>
        <li>
          <strong>30-day tiered default outcome</strong> — if no mutual
          agreement is reached within 30 days of the dispute being filed,
          the platform mechanically applies the published tier rule below.
          This is NOT a judgment call: the outcome is determined entirely
          by objective timestamps (when the dispute was filed relative to
          the booked pickup time) and the order&rsquo;s own status field
          (whether the transporter has marked the trip as in-transit /
          delivered).
          <ul>
            <li>
              <strong>Tier 1 (filed &gt; 72h before booked pickup):</strong>
              full refund to buyer. No transporter work yet incurred.
            </li>
            <li>
              <strong>Tier 2 (filed 24–72h before booked pickup):</strong>
              50% refund to buyer, 50% release to transporter. Mirrors
              the §9.1 buyer-cancellation schedule.
            </li>
            <li>
              <strong>Tier 3 (filed &lt; 24h before pickup, OR after the
              transporter has marked the trip &ldquo;in transit,&rdquo;
              delivered, or completed):</strong> full release to
              transporter. Their costs are real once they are on the
              road or about to load. Note: opening a tier-3 dispute
              alleging negligence, fraud, or material misrepresentation
              still allows the buyer to pursue a Stripe chargeback in
              parallel — the chargeback is judged by the card issuer,
              not by Armstrong.
            </li>
          </ul>
        </li>
        <li>
          <strong>Withdrawal</strong> — either party may withdraw their
          dispute at any time. If withdrawn by the buyer, the escrow
          auto-release timer resumes. If withdrawn by the transporter without
          buyer agreement, the dispute remains open until the timeline above
          resolves it.
        </li>
      </ul>
      <p>
        Armstrong&rsquo;s role is limited to hosting the dispute messaging
        channel between the parties, running the countdown timer, and
        executing the mechanical refund or release when one of the above
        conditions is met. We do not read evidence, weigh fault, render
        decisions, or pick sides. Chargebacks remain available as a parallel
        recourse and are assessed against the transporter&rsquo;s Stripe
        Connect account.
      </p>

      <h3>9.4 Delay flags</h3>
      <p>
        Transporters may flag a delivery delay (weather, mechanical issue,
        buyer unreachable, requested reschedule, or other) at any point
        during a trip. Each active flag pauses the 48-hour escrow
        auto-release clock and extends it by 24 hours. Flags do not by
        themselves entitle either party to a refund &mdash; they are a
        transparency mechanism. If a flag persists for more than 5 business
        days without being cleared, either party may escalate to the Dispute
        process.
      </p>

      <h3>9.5 Stripe Connect &amp; platform fee</h3>
      <p>
        Buyers are charged via Stripe Checkout using a destination charge to
        the transporter&rsquo;s Stripe Connect Express account. A flat 10%
        platform fee is collected by Armstrong K9 Registry on every completed
        trip to fund verification, escrow administration, insurance auditing, and
        customer support. The platform fee is refunded automatically on any
        full Stripe refund (whether buyer cancellation in tier 1, transporter
        cancellation, or the 30-day default outcome of a dispute).
      </p>

      <h2 id="background-check">9a. Provider background-check badge ($39 service fee)</h2>
      <p>
        The optional $39 background-check badge for service providers
        (trainers, groomers, walkers, sitters) is a service fee that
        covers Armstrong&rsquo;s per-check pass-through cost to{" "}
        <strong>Checkr, Inc.</strong> plus the workflow administration
        (consent capture, FCRA disclosures, pre-adverse and adverse-
        action notices, dispute reinstatement). The full process is
        documented in the{" "}
        <a
          href="/api/providers/background-check-policy.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          Background Check &amp; FCRA Adverse Action Policy
        </a>.
      </p>
      <ul>
        <li>
          <strong>Before Checkr invitation is sent</strong> (typically
          a 30-minute window after Stripe capture): <strong>full
          refund</strong> within 1 business day on provider request.
        </li>
        <li>
          <strong>After Checkr invitation is sent, regardless of
          outcome</strong> (Clear, Consider, or Suspended):{" "}
          <strong>non-refundable</strong>. Armstrong has incurred the
          pass-through cost and performed the workflow service. A
          &ldquo;Consider&rdquo; or &ldquo;Suspended&rdquo; outcome
          is a valid output of the service the provider purchased,
          not a service failure.
        </li>
        <li>
          <strong>Successful candidate dispute through Checkr.</strong>{" "}
          If Checkr revises a report to &ldquo;Clear&rdquo; after a
          successful dispute, Armstrong reinstates the badge at{" "}
          <strong>no re-run charge</strong>. No refund is issued (the
          original service was performed), but the provider receives
          the badge they paid for at no additional cost.
        </li>
        <li>
          <strong>Chargebacks.</strong> A provider who initiates a
          chargeback on the $39 SKU after the Checkr invitation has
          been sent will have any active badge revoked while the
          chargeback is pending, regardless of the report outcome.
          See §12.
        </li>
      </ul>

      <h2>10. Promo codes</h2>
      <p>
        Discounts applied via promo code are not refundable as cash. If a
        refund is granted, only the amount actually paid is returned and the
        redemption is reversed.
      </p>

      <h2>11. How to request a refund</h2>
      <p>
        Email{" "}
        <a href="mailto:support@armstrongk9registry.com">
          support@armstrongk9registry.com
        </a>{" "}
        from the email tied to your account, including:
      </p>
      <ul>
        <li>
          Order ID or session ID (visible on{" "}
          <Link to="/billing">Billing → Payment history</Link>).
        </li>
        <li>Reason for the refund.</li>
        <li>
          Whether you&rsquo;d prefer a refund to your original payment method
          or credit toward a future purchase (when applicable).
        </li>
      </ul>
      <p>
        We acknowledge requests within 2 business days and process approved
        refunds within 5&ndash;10 business days back to the original card.
        Stripe-issued refunds typically appear on bank statements within
        5&ndash;10 business days after we trigger them.
      </p>

      <h2>12. Chargebacks</h2>
      <p>
        Please contact us before initiating a chargeback. We work with most
        billing concerns directly and a chargeback can result in suspension
        of your account while the dispute is investigated. Repeated
        unjustified chargebacks may lead to permanent termination per the{" "}
        <Link to="/terms">Terms of Service</Link>.
      </p>
    </LegalLayout>
  );
}
